import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Tooltip,
  Tab,
  Tabs
} from '@mui/material';
import {
  People,
  Assessment,
  TrendingUp,
  Security,
  AccountBalance,
  Timeline,
  MoreVert,
  BarChart,
  PieChart,
  Refresh
} from '@mui/icons-material';
import axios from 'axios';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';

interface DashboardStats {
  total_users: number;
  active_sessions: number;
  system_status: string;
  last_updated: string;
}

interface WelcomeMessage {
  message: string;
  user: string;
  timestamp: string;
}

interface MasterDataSummary {
  year: number;
  record_count: number;
}

interface RatingComponentData {
  component: string;
  weight: number;
  r_square: number;
}

interface PDDistribution {
  range: string;
  count: number;
  percentage: number;
}

interface MonthlyActivity {
  month: string;
  assessments: number;
  avg_pd: number;
}

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe'];

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState<WelcomeMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Chart data states
  const [masterDataSummary, setMasterDataSummary] = useState<MasterDataSummary[]>([]);
  const [ratingComponents, setRatingComponents] = useState<RatingComponentData[]>([]);
  const [pdDistribution, setPDDistribution] = useState<PDDistribution[]>([]);
  const [monthlyActivity, setMonthlyActivity] = useState<MonthlyActivity[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const [statsResponse, welcomeResponse] = await Promise.all([
        axios.get('/api/v1/dashboard/stats', config),
        axios.get('/api/v1/dashboard/welcome', config)
      ]);

      setStats(statsResponse.data);
      setWelcomeMessage(welcomeResponse.data);
      
      await fetchMasterDataSummary(config);
      await fetchRatingComponents(config);
      generatePDDistribution();
      generateMonthlyActivity();

      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
      setLoading(false);
    }
  };

  const fetchMasterDataSummary = async (config: any) => {
    try {
      const response = await axios.get('/api/v1/master-data', config);
      if (response.data?.data) {
        const yearCounts: Record<number, number> = {};
        response.data.data.forEach((item: any) => {
          yearCounts[item.year] = (yearCounts[item.year] || 0) + 1;
        });
        
        const summary: MasterDataSummary[] = Object.entries(yearCounts)
          .map(([year, count]) => ({ year: parseInt(year), record_count: count }))
          .sort((a, b) => b.year - a.year);
        
        setMasterDataSummary(summary);
      }
    } catch (err) {
      console.error('Error fetching master data summary:', err);
      setMasterDataSummary([
        { year: 2024, record_count: 156 },
        { year: 2023, record_count: 234 },
        { year: 2022, record_count: 189 },
        { year: 2021, record_count: 145 },
        { year: 2020, record_count: 98 }
      ]);
    }
  };

  const fetchRatingComponents = async (config: any) => {
    try {
      const response = await axios.get('/api/v1/analytics/rating-components', config);
      if (response.data?.components) {
        const components: RatingComponentData[] = response.data.components.map((comp: any) => ({
          component: comp.display_name || comp.component,
          weight: comp.weights * 100,
          r_square: comp.r_square * 100
        }));
        setRatingComponents(components);
      }
    } catch (err) {
      console.error('Error fetching rating components:', err);
      setRatingComponents([
        { component: 'Monthly Salary', weight: 9.22, r_square: 90.3 },
        { component: 'Post Money DSR', weight: 8.47, r_square: 82.9 },
        { component: 'Education Level', weight: 9.18, r_square: 89.9 },
        { component: 'Occupation', weight: 9.72, r_square: 95.2 },
        { component: 'Company Listing', weight: 9.13, r_square: 89.4 },
        { component: 'Service Length - Current', weight: 8.94, r_square: 87.5 },
        { component: 'Service Length - UAE', weight: 8.26, r_square: 80.8 },
        { component: 'Job Type', weight: 7.74, r_square: 75.8 },
        { component: 'Nationality', weight: 7.27, r_square: 71.2 },
        { component: 'Number of Dependents', weight: 6.89, r_square: 67.5 },
        { component: 'Age', weight: 6.96, r_square: 68.1 },
        { component: 'Marital Status', weight: 8.23, r_square: 80.5 }
      ]);
    }
  };

  const generatePDDistribution = () => {
    setPDDistribution([
      { range: '0-1% (Low)', count: 45, percentage: 35.4 },
      { range: '1-2% (Med-Low)', count: 38, percentage: 29.9 },
      { range: '2-5% (Medium)', count: 25, percentage: 19.7 },
      { range: '5-10% (Med-High)', count: 12, percentage: 9.4 },
      { range: '10%+ (High)', count: 7, percentage: 5.5 }
    ]);
  };

  const generateMonthlyActivity = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date().getMonth();
    
    const activity: MonthlyActivity[] = [];
    for (let i = 0; i < 12; i++) {
      const monthIndex = (currentMonth - 11 + i + 12) % 12;
      activity.push({
        month: months[monthIndex],
        assessments: Math.floor(Math.random() * 50) + 20,
        avg_pd: parseFloat((Math.random() * 0.03 + 0.01).toFixed(4))
      });
    }
    setMonthlyActivity(activity);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 4 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  return (
    <Box>
      {/* Welcome Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Risk Analytics Dashboard
          </Typography>
          {welcomeMessage && (
            <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{welcomeMessage.message}</Typography>
            </Alert>
          )}
        </Box>
        <Tooltip title="Refresh dashboard data">
          <IconButton onClick={fetchDashboardData} color="primary"><Refresh /></IconButton>
        </Tooltip>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {masterDataSummary.reduce((sum, d) => sum + d.record_count, 0)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Records</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <People sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption">+12% from last year</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {ratingComponents.length || 12}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Risk Components</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <Assessment sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>Optimal</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Model Status</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <Security sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>98.5%</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Accuracy Rate</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <AccountBalance sx={{ fontSize: 28 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Analytics Section with Tabs */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto">
                  <Tab icon={<BarChart />} label="Master Data Trend" iconPosition="start" />
                  <Tab icon={<PieChart />} label="PD Distribution" iconPosition="start" />
                  <Tab icon={<Assessment />} label="Component Weights" iconPosition="start" />
                  <Tab icon={<Timeline />} label="Monthly Activity" iconPosition="start" />
                </Tabs>
              </Box>

              <Box sx={{ p: 3, height: 350 }}>
                {/* Tab 1: Master Data Trend */}
                {activeTab === 0 && (
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart data={masterDataSummary} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis />
                      <RechartsTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                      <Legend />
                      <Bar dataKey="record_count" name="Records" fill="#667eea" radius={[4, 4, 0, 0]} />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                )}

                {/* Tab 2: PD Distribution */}
                {activeTab === 1 && (
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={pdDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ range, percentage }) => `${range}: ${percentage}%`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {pdDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                )}

                {/* Tab 3: Component Weights */}
                {activeTab === 2 && (
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart data={ratingComponents} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 12]} />
                      <YAxis type="category" dataKey="component" width={120} />
                      <RechartsTooltip contentStyle={{ borderRadius: 8 }} />
                      <Legend />
                      <Bar dataKey="weight" name="Weight (%)" fill="#764ba2" radius={[0, 4, 4, 0]} />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                )}

                {/* Tab 4: Monthly Activity */}
                {activeTab === 3 && (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthlyActivity} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <RechartsTooltip contentStyle={{ borderRadius: 8 }} />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="assessments" name="Assessments" stroke="#667eea" fill="#667eea" fillOpacity={0.3} />
                      <Line yAxisId="right" type="monotone" dataKey="avg_pd" name="Avg PD" stroke="#f5576c" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Recent Activity</Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                {[
                  { title: 'New risk assessment', time: '2 mins ago', icon: <Assessment /> },
                  { title: 'Master data updated', time: '15 mins ago', icon: <People /> },
                  { title: 'Report generated', time: '1 hour ago', icon: <BarChart /> },
                  { title: 'Model recalibrated', time: '3 hours ago', icon: <Security /> }
                ].map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.light', width: 36, height: 36 }}>{item.icon}</Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={item.title} secondary={item.time} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
